import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";

import { apiClient } from "@/api";
import { Cohort, User } from "@/api/types";
import CohortTile from "@/components/users/CohortTile";
import UserTile, { getUserDisplayName } from "@/components/users/UserTile";

interface CohortUserMappingProps {}

const parseCohortsResponse = (payload: unknown): Cohort[] => {
  if (Array.isArray(payload)) {
    return payload as Cohort[];
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const maybeObject = payload as { cohorts?: Cohort[]; results?: Cohort[] };
  if (Array.isArray(maybeObject.cohorts)) {
    return maybeObject.cohorts;
  }
  if (Array.isArray(maybeObject.results)) {
    return maybeObject.results;
  }

  return [];
};

const parseUsersResponse = (payload: unknown): User[] => {
  if (Array.isArray(payload)) {
    return payload as User[];
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const maybeObject = payload as { users?: User[]; results?: User[] };
  if (Array.isArray(maybeObject.users)) {
    return maybeObject.users;
  }
  if (Array.isArray(maybeObject.results)) {
    return maybeObject.results;
  }

  return [];
};

const CohortUserMapping: React.FC<CohortUserMappingProps> = () => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingUserIds, setUpdatingUserIds] = useState<Set<number>>(new Set());

  const [showNewCohortModal, setShowNewCohortModal] = useState<boolean>(false);
  const [editingCohort, setEditingCohort] = useState<Cohort | null>(null);
  const [newCohortName, setNewCohortName] = useState<string>("");
  const [newCohortDescription, setNewCohortDescription] = useState<string>("");
  const [creatingCohort, setCreatingCohort] = useState<boolean>(false);
  const [deletingCohort, setDeletingCohort] = useState<boolean>(false);
  const [newCohortError, setNewCohortError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const selectedCohort = useMemo(
    () => cohorts.find((cohort) => cohort.id === selectedCohortId) || null,
    [cohorts, selectedCohortId]
  );

  const usersInSelectedCohort = useMemo(() => {
    if (!selectedCohortId) {
      return [] as User[];
    }

    return users.filter((user) =>
      (user.cohorts || []).some((cohort) => cohort.id === selectedCohortId)
    );
  }, [users, selectedCohortId]);

  const usersOutsideSelectedCohort = useMemo(() => {
    if (!selectedCohortId) {
      return [] as User[];
    }

    return users.filter(
      (user) => !(user.cohorts || []).some((cohort) => cohort.id === selectedCohortId)
    );
  }, [users, selectedCohortId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [cohortPayload, userPayload] = await Promise.all([
        apiClient.get<unknown>("/cohorts/"),
        apiClient.get<unknown>("/users/"),
      ]);

      const nextCohorts = parseCohortsResponse(cohortPayload);
      const nextUsers = parseUsersResponse(userPayload);

      setCohorts(nextCohorts);
      setUsers(nextUsers);

      if (nextCohorts.length > 0) {
        setSelectedCohortId((prev) => {
          if (prev && nextCohorts.some((cohort) => cohort.id === prev)) {
            return prev;
          }
          return nextCohorts[0].id;
        });
      } else {
        setSelectedCohortId(null);
      }
    } catch (exc) {
      console.error("Failed to fetch cohorts and users", exc);
      setError("Unable to load cohorts and users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openNewCohortModal = useCallback(() => {
    setEditingCohort(null);
    setNewCohortName("");
    setNewCohortDescription("");
    setNewCohortError(null);
    setShowNewCohortModal(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  }, []);

  const openEditCohortModal = useCallback((cohort: Cohort) => {
    setEditingCohort(cohort);
    setNewCohortName(cohort.name);
    setNewCohortDescription(cohort.description || "");
    setNewCohortError(null);
    setShowNewCohortModal(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  }, []);

  const closeNewCohortModal = useCallback(() => {
    setShowNewCohortModal(false);
    setEditingCohort(null);
  }, []);

  const handleSaveCohort = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedName = newCohortName.trim();
      if (!trimmedName) {
        setNewCohortError("Name is required.");
        nameInputRef.current?.focus();
        return;
      }

      setCreatingCohort(true);
      setNewCohortError(null);

      try {
        const payload: { name: string; description?: string } = { name: trimmedName, description: newCohortDescription.trim() };

        if (editingCohort) {
          const updated = await apiClient.patch<Cohort>(`/cohorts/${editingCohort.id}/`, payload);
          setCohorts((prev) =>
            prev.map((cohort) => (cohort.id === updated.id ? updated : cohort))
          );
          setSelectedCohortId(updated.id);
        } else {
          // we need to submit 'users' since it's required, even though we don't actually want to set any users at this point
          const created = await apiClient.post<Cohort>("/cohorts/", { users: [], ...payload } );
          setCohorts((prev) => [...prev, created]);
          setSelectedCohortId(created.id);
        }

        setShowNewCohortModal(false);
      } catch (exc) {
        console.error("Failed to save cohort", exc);
        setNewCohortError("Failed to save cohort. Please try again.");
      } finally {
        setCreatingCohort(false);
      }
    },
    [newCohortName, newCohortDescription, editingCohort]
  );

  const handleDeleteCohort = useCallback(async () => {
    if (!editingCohort) {
      return;
    }

    const confirmed = window.confirm(`Delete cohort \"${editingCohort.name}\"?`);
    if (!confirmed) {
      return;
    }

    setDeletingCohort(true);
    setNewCohortError(null);

    try {
      await apiClient.delete(`/cohorts/${editingCohort.id}/`);
      setCohorts((prev) => prev.filter((cohort) => cohort.id !== editingCohort.id));
      setUsers((prev) =>
        prev.map((user) => ({
          ...user,
          cohorts: (user.cohorts || []).filter((cohort) => cohort.id !== editingCohort.id),
        }))
      );
      setSelectedCohortId((prev) => (prev === editingCohort.id ? null : prev));
      setShowNewCohortModal(false);
      setEditingCohort(null);
    } catch (exc) {
      console.error("Failed to delete cohort", exc);
      setNewCohortError("Failed to delete cohort. Please try again.");
    } finally {
      setDeletingCohort(false);
    }
  });

  const handleModalKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        closeNewCohortModal();
      }
    },
    [closeNewCohortModal]
  );

  const updateCohortMembership = useCallback(
    async (user: User, shouldAdd: boolean) => {
      if (!selectedCohortId) {
        return;
      }

      setUpdatingUserIds((prev) => {
        const next = new Set(prev);
        next.add(user.id);
        return next;
      });

      const currentCohortIds = (user.cohorts || []).map((cohort) => cohort.id);
      const nextCohortIds = shouldAdd
        ? Array.from(new Set([...currentCohortIds, selectedCohortId]))
        : currentCohortIds.filter((cohortId) => cohortId !== selectedCohortId);

      try {
        const updatedUser = await apiClient.patch<User>(`/users/${user.id}/`, {
          cohort_ids: nextCohortIds,
        });

        setUsers((prev) => prev.map((entry) => (entry.id === user.id ? updatedUser : entry)));
      } catch (exc) {
        console.error("Failed to update cohort membership", exc);
      } finally {
        setUpdatingUserIds((prev) => {
          const next = new Set(prev);
          next.delete(user.id);
          return next;
        });
      }
    },
    [selectedCohortId]
  );

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
        Loading cohorts and users...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {showNewCohortModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onKeyDown={handleModalKeyDown}
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeNewCohortModal}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-cohort-title"
            className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="new-cohort-title" className="text-lg font-semibold text-gray-800">
                {editingCohort ? "Edit Cohort Details" : "New Cohort"}
              </h2>
              <button
                type="button"
                onClick={closeNewCohortModal}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cancel"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSaveCohort} noValidate>
              <div className="mb-4">
                <label
                  htmlFor="new-cohort-name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="new-cohort-name"
                  ref={nameInputRef}
                  type="text"
                  value={newCohortName}
                  onChange={(e) => setNewCohortName(e.target.value)}
                  required
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="e.g. Spring 2026"
                />
              </div>

              <div className="mb-5">
                <label
                  htmlFor="new-cohort-description"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Description
                  <span className="ml-1 text-xs font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="new-cohort-description"
                  value={newCohortDescription}
                  onChange={(e) => setNewCohortDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="A short description of this cohort..."
                />
              </div>

              {newCohortError && (
                <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
                  {newCohortError}
                </p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeNewCohortModal}
                  disabled={creatingCohort || deletingCohort}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                {editingCohort && (
                  <button
                    type="button"
                    onClick={handleDeleteCohort}
                    disabled={creatingCohort || deletingCohort}
                    className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    {deletingCohort ? "Deleting..." : "Delete"}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={creatingCohort || deletingCohort}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingCohort
                    ? editingCohort
                      ? "Saving..."
                      : "Creating..."
                    : editingCohort
                      ? "Save Cohort"
                      : "Create Cohort"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <div className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-1">
        <div className="mb-3 text-sm font-semibold text-gray-600">Cohorts</div>

        <div className="space-y-2">
          {cohorts.length === 0 && (
            <div className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500">
              No cohorts found.
            </div>
          )}

          {cohorts.map((cohort) => {
            const isSelected = selectedCohortId === cohort.id;
            return (
              <CohortTile
                key={cohort.id}
                cohort={cohort}
                selected={isSelected}
                onClick={() => setSelectedCohortId(cohort.id)}
                className="w-full"
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={openNewCohortModal}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
        >
          <FontAwesomeIcon icon={faPlus} />
          New Cohort
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-2">
        {!selectedCohort ? (
          <div className="flex h-full min-h-48 items-center justify-center rounded-md border border-dashed border-gray-300 text-sm text-gray-500">
            Select a cohort to manage users.
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-800">{selectedCohort.name}</h3>
                <button
                  type="button"
                  onClick={() => openEditCohortModal(selectedCohort)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Edit Cohort Details
                </button>
              </div>
              <p className="text-sm text-gray-500">
                {usersInSelectedCohort.length} member{usersInSelectedCohort.length === 1 ? "" : "s"} in cohort
              </p>
            </div>

            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-700">In cohort</h4>
              {usersInSelectedCohort.length === 0 ? (
                <div className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500">
                  No users in this cohort yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {usersInSelectedCohort.map((user) => {
                    const isUpdating = updatingUserIds.has(user.id);
                    return (
                      <UserTile
                        key={user.id}
                        user={user}
                        disabled={isUpdating}
                        onActionClick={() => updateCohortMembership(user, false)}
                        actionIcon={faTimes}
                        actionAriaLabel={`Remove ${getUserDisplayName(user)} from cohort`}
                      />
                    );
                  })}
                </div>
              )}
            </section>

            <section>
              <h4 className="mb-2 text-sm font-semibold text-gray-700">Not in cohort</h4>
              {usersOutsideSelectedCohort.length === 0 ? (
                <div className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500">
                  All users are in this cohort.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {usersOutsideSelectedCohort.map((user) => {
                    const isUpdating = updatingUserIds.has(user.id);
                    return (
                      <UserTile
                        key={user.id}
                        user={user}
                        onClick={() => updateCohortMembership(user, true)}
                        disabled={isUpdating}
                        muted={true}
                        dashed={true}
                        cornerIcon={faPlus}
                        cornerIconClassName="text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-700"
                        className="group"
                      />
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default CohortUserMapping;
